import React, { useState } from 'react';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FuseInput } from 'src/components';
import { useAccountStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { AccountTransaction, Lender } from '/src/lib/models';

export interface AccountLedgerModalProps {
  /**
   * The account ledger's associated Lender
   */
  lender: Lender;
  /**
   * Sets the account ledger's associated Lender
   */
  setLender: (lender: Lender) => void;
  /**
   * Transaction item to display
   */
  item?: AccountTransaction;
  /**
   * Whether this component is visible
   */
  open: boolean;
  /**
   * Sets component visibility
   * @returns void
   */
  setOpen: (open: boolean) => void;
}

export const AccountLedgerModal: React.FC<AccountLedgerModalProps> = observer(
  ({ lender, setLender, item, open, setOpen }) => {
    /** Context **/
    const accountStore = useAccountStore();

    /** State **/
    const [amount, setAmount] = useState(item?.amount);
    const [oldAmount] = useState(item?.amount);
    const [effectiveDate, setEffectiveDate] = useState(item ? dayjs(item.effectiveDate).toDate() : dayjs().toDate());
    const [maxError, setMaxError] = useState<boolean>(false);

    /**
     * Remove a Ledger item
     */
    const removeLedgerItem = async () => {
      if (!item) return;

      try {
        // Create new withdrawal/deposit object
        const ledgerItem = {
          _id: lender._id,
          transactionId: item._id,
        };

        // Remove it
        const account = await accountStore.remove(lender._id, ledgerItem.transactionId);

        // Store updated account items list
        setLender({ ...lender, account });

        // Confirmation alert
        toast.success(toastMessages.deleteTransaction.success);

        // Close dialog
        setOpen(false);
      } catch (err) {
        console.error(err);
        toast.error(toastMessages.deleteTransaction.error);
      }
    };

    /**
     * Updates a Ledger item
     */
    const updateLedgerItem = async () => {
      if (!item) return;
      if (!amount) throw new Error('An amount is required');

      if (amount === oldAmount) {
        setOpen(false);
        return;
      }

      // Check to see if form is valid
      // $lenderLedgerForm.$setDirty();
      // if (!$lenderLedgerForm.$valid) {
      //   Alerts.show('Please fix the errors first.');
      //   return false;
      // }

      try {
        const account = await accountStore.update(lender._id, item._id, {
          amount,
        });

        // Store updated account items list
        setLender({ ...lender, account });

        // Confirmation alert
        toast.success(toastMessages.updateTransaction.success);

        // Close dialog
        setOpen(false);
      } catch (err) {
        console.error(err);
        toast.error(toastMessages.updateTransaction.error);
      }
    };

    /**
     * Creates a Ledger item
     */
    const createLedgerItem = async () => {
      if (!amount) throw new Error('An amount is required');
      if (!effectiveDate) throw new Error('Effective date is required');

      // // Check to see if form is valid
      // $scope.lenderLedgerForm.$setDirty();
      // if(!$scope.lenderLedgerForm.$valid){
      //     Alerts.show('Please fix the errors first.');
      //     scope.maxError = true;
      //     return;
      // }

      setMaxError(false);

      try {
        // Update it
        const account = await accountStore.create(lender._id, {
          amount,
          effectiveDate,
        });

        // Store updated account items list
        setLender({ ...lender, account });

        // Confirmation alert
        toast.success(toastMessages.createTransaction.success);

        // Close dialog
        setOpen(false);
      } catch (err) {
        console.error(err);
        toast.error(toastMessages.createTransaction.error);
      }
    };

    // const handleAmountChange = (value?: string) => {
    //   isNumber(value) && setAmount(parseFloat(value ?? '0'));
    // };

    const handleDateChange = (value?: string) => {
      if (!value) {
        setMaxError(false);
        setEffectiveDate(new Date());
        return;
      }

      const selectedDate = new Date(value);
      const today = new Date();
      if (selectedDate > today) {
        setMaxError(true);
      } else {
        setMaxError(false);
        setEffectiveDate(selectedDate);
      }
    };

    /** Render **/
    return (
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        // maxWidth="xs"
        // fullWidth
        aria-label={`${item ? 'Edit' : 'Post'} Transaction`}
      >
        <Box className="toolbar-div md-toolbar-tools" width="500px" display="flex" justifyContent="space-between">
          <Typography variant="h2" className="md-subhead capitalize">
            {item ? 'Edit' : 'Post'} Transaction
          </Typography>
          <Box display="flex" />
          <IconButton className="md-icon-button" onClick={() => setOpen(false)} aria-label="close">
            <CloseIcon className="md-icon material-icons" sx={{ color: 'white' }} />
          </IconButton>
        </Box>
        <DialogContent>
          <FuseInput
            label={`Amount ${item ? '(Enter $0 To Delete)' : ''}`}
            value={amount}
            updateValue={(value) => setAmount(value)}
            type="number"
            required
          />
          {!item && (
            <FuseInput
              label="Effective Date"
              type="date"
              value={effectiveDate}
              updateValue={handleDateChange}
              required
            />
          )}
          {maxError && (
            <Typography color="error" align="center">
              Please select a date that's not in the future.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Cancel
          </Button>
          {item && (
            <Button
              onClick={() => (amount === 0 ? removeLedgerItem() : updateLedgerItem())}
              color={amount === 0 ? 'error' : 'primary'}
            >
              {amount === 0 ? 'Delete' : 'Update'}
            </Button>
          )}
          {!item && (
            <Button onClick={() => createLedgerItem()} color="primary">
              Submit
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);
